import { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import PopupResult from '@r1-frontend/ui-react/components/popups/popupResult';
import { InfoCircleFlip } from '@r1-frontend/ui-react/components/svg/main';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { pluralize } from '@r1-frontend/shared/helpers/pluralize';
import { channelPluralize, dayPluralize, monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import Widget from '~/src/components/Widget';
import { USER_LOGIN_ROUTE } from '~/src/constants/routes';
import PackageConnectPopup from '~/src/features/domru-tv/packages/components/PackageConnectPopup';
import { defaultResultPopupState } from '~/src/features/domru-tv/packages/types';
import { dateWithoutYear } from '~/src/helpers/date';
import { TState } from '~/src/store';
import { fetchProfileTvPackages } from '~/src/store/actions/tvPackages';
import { loadVas } from '~/src/store/actions/vas';
import { ETryAndBuyDurationTypeId, ETvPackagesActionTypes, ITvPackage } from '~/src/store/reducers/tvPackages';

import LabelAndActivity from './LabelAndActivity';

import { ButtonsRow, PopupContent } from '~/src/features/domru-tv/packages/components/PackageConnectPopup/styled';
import * as ST from './styled';

interface IProps {
    tvPacket: ITvPackage,
}

const TvPackageCard = ({ tvPacket }: IProps): JSX.Element => {
    const {
        id,
        active,
        available,
        alias,
        baseCost = 0,
        channelsCount,
        channelsCountHd,
        cost,
        costRa = [],
        description,
        image,
        name,
        state: packageState,
        tryAndBuy,
    } = tvPacket;

    const dispatch = useDispatch();

    const router = useRouter();

    const { isAuth } = useSelector((state: TState) => state.auth);

    const packetPrices = useMemo(() => {
        return active
            ? costRa
            : [
                {
                    cost,
                    duration: 0,
                    durationTypeId: 0,
                    groups: [],
                    raId: 0,
                },
                ...costRa,
            ];
    }, [active, cost, costRa]);

    const tryAndBuyLabel = useMemo(() => {
        if (packageState?.actDateTo) {
            return `промо-цена до ${dateWithoutYear(new Date(packageState.actDateTo * 1000))}`;
        }

        if (tryAndBuy) {
            const { duration, durationTypeId } = tryAndBuy;
            const periodText = durationTypeId === ETryAndBuyDurationTypeId.DAYS
                ? `${duration} ${pluralize(duration, dayPluralize)}`
                : `${duration} ${pluralize(duration, monthPluralize)}`;

            return `${periodText} бесплатно`;
        }
    }, [packageState, tryAndBuy]);

    const [isRequestPopupOpen, setIsRequestPopupOpen] = useState(false);
    const [resultPopupState, setResultPopupState] = useState(defaultResultPopupState);

    const onConnectHandler = useCallback(() => {
        isAuth
            ? setIsRequestPopupOpen(true)
            : router.push(`${USER_LOGIN_ROUTE}?referrer=${location.pathname}`);

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: active ? 'click_renew_subscription_open' : 'click connect btn',
            label: `package: ${name}`,
        });
    }, [isAuth, router]);

    const openTnBСonnectPopup = () => {
        dispatch({
            type: ETvPackagesActionTypes.SET_TRIAL_PACKAGE,
            payload: {
                trialPackage: tvPacket,
            },
        });
        setIsRequestPopupOpen(true);
    };

    const onDetailHandler = useCallback(() => {
        router.push(alias);

        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.vas,
            action: 'click_know_more',
            label: `package: ${name}`,
        });
    }, [alias, router]);

    const onCloseResultHandler = useCallback(() => {
        if (resultPopupState.type === 'success') {
            dispatch(fetchProfileTvPackages());
            dispatch(loadVas());
        }

        setResultPopupState(defaultResultPopupState);
    }, [dispatch, resultPopupState.type]);

    const onBalanceReplenish = useCallback(() => {
        onCloseResultHandler();
        router.push('/payments');
    }, [onCloseResultHandler, router]);

    const isAvailableAndActive = active && available;

    return (
        <>
            <Widget>
                <Widget.ImageBlock>
                    <Widget.Image src={image} alt={name}/>
                </Widget.ImageBlock>
                <ST.WidgetContent>
                    <LabelAndActivity
                        isActive={!!active}
                        tvPacketState={packageState}
                        tryAndBuyLabel={tryAndBuyLabel}
                    />
                    <Widget.Name>{name}</Widget.Name>
                    <ST.PackageInfo>
                        <ST.PackageDesc dangerouslySetInnerHTML={{ __html: description }}/>
                        <ST.PackageChannelsCount>
                            {channelsCount} {pluralize(channelsCount, channelPluralize)} ({channelsCountHd} HD)
                        </ST.PackageChannelsCount>
                    </ST.PackageInfo>
                    <Widget.FooterBlock>
                        <ST.WidgetPrice isPromoPrice={cost < baseCost}>{cost} ₽/мес</ST.WidgetPrice>
                        {
                            isAvailableAndActive && !costRa.length
                                ? <Button
                                    bType={'secondary'}
                                    size={'small'}
                                    onClick={onDetailHandler}
                                    data-test='details-button'
                                >
                                    Подробнее
                                </Button>
                                : available !== 0
                                    ? <ST.ButtonsWrapper>
                                        <Button
                                            bType={active ? 'secondary' : 'primary'}
                                            size={'small'}
                                            onClick={tryAndBuy ? openTnBСonnectPopup : onConnectHandler}
                                            data-test={`${active ? 'renew' : 'connect'}-button`}
                                        >
                                            {active ? 'Продлить' : 'Подключить'}
                                        </Button>
                                        <Button
                                            size={'small'}
                                            bType={'secondary'}
                                            Icon={<InfoCircleFlip size={20}/>}
                                            onClick={onDetailHandler}
                                            rounded
                                            data-test='widget-details'
                                        />
                                    </ST.ButtonsWrapper>
                                    : null
                        }
                    </Widget.FooterBlock>
                </ST.WidgetContent>
            </Widget>
            {(!active || !!packetPrices.length) && (
                <>
                    <PackageConnectPopup
                        isOpen={isRequestPopupOpen}
                        isPackageConnected={!!active}
                        packetId={id}
                        packageName={name}
                        prices={packetPrices}
                        setResult={setResultPopupState}
                        onClose={() => setIsRequestPopupOpen(false)}
                        data-test='connect-popup'
                    />
                    <PopupResult {...resultPopupState} onClose={onCloseResultHandler}>
                        <PopupContent>
                            <ButtonsRow>
                                {resultPopupState.isBalanceError && (
                                    <Button
                                        bType={'action'}
                                        size={'large'}
                                        onClick={onBalanceReplenish}
                                    >
                                        Пополнить баланс
                                    </Button>
                                )}
                                <Button
                                    bType={resultPopupState.isBalanceError ? 'secondary' : 'action'}
                                    size={'large'}
                                    onClick={onCloseResultHandler}
                                >
                                    Закрыть
                                </Button>
                            </ButtonsRow>
                        </PopupContent>
                    </PopupResult>
                </>
            )}
        </>
    );
};

export default memo(TvPackageCard);
